import { createRouter, createWebHistory } from "vue-router";
import store from "@/store/index";

// public pages
import LandingPageView from "@/pages/index.vue";
import TermsView from "@/pages/public/Terms.vue";
import PolicyView from "@/pages/public/Policy.vue";
import SupportView from "@/pages/public/Support.vue";
import PricingView from "@/pages/public/Pricing.vue";
import PublicProfileView from "@/pages/public/Profile.vue";
import AboutView from "@/pages/public/resources/About.vue";

// Product pages (public)
import ProjectManagementView from "@/components/products/ProjectManagement.vue";
import ProductIntegrationsView from "@/components/products/Integrations.vue";
import ClientManagementView from "@/components/products/ClientManagement.vue";
import ProductProposalView from "@/components/products/ProductProposals.vue";
import WorkflowsView from "@/components/products/Workflows.vue";
import ProductReportsView from "@/components/products/Reports.vue";
import RemindersView from "@/components/products/CustomReminders.vue";
import ProductFinanceView from "@/components/products/Finance.vue";

// Auth pages
import LoginView from "@/pages/auth/Login.vue";
import InitiateResetView from "@/pages/auth/InitiateReset.vue";
import SignupView from "@/pages/auth/Signup.vue";
import ResetView from "@/pages/auth/Reset.vue";
import AuthHandlerView from "@/pages/auth/ProviderCallback.vue";
import LogoutPage from "@/pages/auth/Logout.vue";

// Dashboard pages
import GettingStartedView from "@/pages/dashboard/getting-started/index.vue";

import ReportsView from "@/pages/dashboard/reports/index.vue";

import BaseRevenuePage from "@/pages/dashboard/revenue/index.vue";
import Stats from "@/pages/dashboard/revenue/RevenueStats.vue";
import History from "@/pages/dashboard/revenue/RevenueHistory.vue";
import Goals from "@/pages/dashboard/revenue/RevenueGoals.vue";

import RateCardView from "@/pages/dashboard/rate-cards/index.vue";

// Verify billings
import InitiatePaidPlanView from "@/pages/dashboard/payments/InitiatePaidPlan.vue";
import VerifyBillingsView from "@/pages/dashboard/payments/Verify.vue";

import MeetingsView from "@/pages/dashboard/meetings/index.vue";

// Clients
import ClientsView from "@/pages/dashboard/clients/index.vue";
import ClientsDetailsView from "@/pages/dashboard/clients/_id.vue";

// Projects
import ProjectsView from "@/pages/dashboard/projects/index.vue";
import ProjectsDetailsView from "@/pages/dashboard/projects/_id/index.vue";
import ProjectTasksView from "@/pages/dashboard/projects/_id/Tasks.vue";

// Settings pages
import AccountSettings from "@/pages/dashboard/settings/Account.vue";
import GeneralSettings from "@/pages/dashboard/settings/GeneralSettings.vue";
import PaymentsAndBillings from "@/pages/dashboard/settings/PaymentsAndBillings.vue";
import PaymentsAccounts from "@/pages/dashboard/settings/PaymentsAccounts.vue";
import GoalsAndProductivity from "@/pages/dashboard/settings/GoalsAndProductivity.vue";
import Plans from "@/pages/dashboard/settings/Plans.vue";
import Notifications from "@/pages/dashboard/settings/Notifications.vue";
import Integrations from "@/pages/dashboard/settings/Integrations.vue";

// Tasks
import TasksView from "@/pages/dashboard/tasks/index.vue";
import TaskDetailsView from "@/components/ui/modals/TaskDetails.vue";

// Invoices
import InvoicesView from "@/pages/dashboard/invoices/index.vue";
import CreateEditInvoiceView from "@/pages/dashboard/invoices/CreateEdit.vue";
import InvoiceDetailsView from "@/pages/dashboard/invoices/_id/index.vue";

const DEFAULT_AUTH_REDIRECT = "login";

const routes = [
    {
        path: "/",
        name: "home",
        component: LandingPageView,
        meta: { requiresAuth: false, title: "Ziner: A smart took-kit for digital creators" },
    },
    {
        path: "/terms",
        name: "terms-and-conditions",
        component: TermsView,
        meta: { requiresAuth: false, title: "Terms of Use" },
    },
    {
        path: "/profile/:name",
        name: "public-profile",
        component: PublicProfileView,
        meta: { requiresAuth: false, title: "Profile" },
    },
    {
        path: "/support",
        name: "support",
        component: SupportView,
        meta: { requiresAuth: false, title: "Support" },
    },
    {
        path: "/about",
        name: "about",
        component: AboutView,
        meta: { requiresAuth: false, title: "About Us" },
    },
    {
        path: "/products/",
        name: "products",
        component: () => import("@/pages/public/products/index.vue"),
        redirect: { name: "product-client-management" },
        children: [
            {
                path: "integrations",
                name: "product-integrations",
                component: ProductIntegrationsView,
                meta: { requiresAuth: false, title: "Products | Integrations" },
            },
            {
                path: "payments-and-finance",
                name: "product-finance",
                component: ProductFinanceView,
                meta: { requiresAuth: false, title: "Products | Payments & Finance" },
            },
            // {
            //     path: "proposals",
            //     name: "product-proposals",
            //     component: ProductProposalView,
            //     meta: { requiresAuth: false, title: "Products | Client Proposals" },
            // },
            {
                path: "reminders",
                name: "product-reminders",
                component: RemindersView,
                meta: { requiresAuth: false, title: "Products | Reminders" },
            },
            {
                path: "workflows",
                name: "product-workflows",
                component: WorkflowsView,
                meta: { requiresAuth: false, title: "Products | Workflows" },
            },
            {
                path: "reports",
                name: "product-reports",
                component: ProductReportsView,
                meta: { requiresAuth: false, title: "Products | Reports" },
            },
            {
                path: "projects-tasks",
                name: "product-project-management",
                component: ProjectManagementView,
                meta: { requiresAuth: false, title: "Products | Project & Tasks" },
            },
            {
                path: "client-management",
                name: "product-client-management",
                component: ClientManagementView,
                meta: { requiresAuth: false, title: "Products | Client Management" },
            },
        ],
    },
    {
        path: "/privacy-policy",
        name: "privacy-policy",
        component: PolicyView,
        meta: { requiresAuth: false, title: "Privacy Policy" },
    },
    {
        path: "/pricing",
        name: "pricing",
        component: PricingView,
        meta: { requiresAuth: false, title: "Pricing" },
    },
    {
        path: "/payments/verify", name: "verify-payments", component: VerifyBillingsView, meta: { requiresAuth: true, title: "Verify Payment Plan" },
    },
    {
        path: "/initiate-plan", name: "initiate-payments", component: InitiatePaidPlanView, meta: { requiresAuth: true, title: "Initiate Paid Plan" },
    },
    {
        path: "/auth/",
        name: "auth",
        component: () => import("@/pages/auth/index.vue"),
        children: [
            {
                path: "login",
                name: "login",
                component: LoginView,
                meta: { requiresAuth: false,  title: "Login", publicAuth: true },
            },
            {
                path: "signup",
                name: "signup",
                component: SignupView,
                meta: { requiresAuth: false,  title: "Signup", publicAuth: true },
        
            },
            {
                path: "reset",
                name: "reset",
                component: ResetView,
                meta: { requiresAuth: false,  title: "Reset Password", publicAuth: true },
            },
            {
                path: "initiate-reset",
                name: "intiate-reset",
                component: InitiateResetView,
                meta: { requiresAuth: false,  title: "Request Password Reset", publicAuth: true },
            },
            { path: "logout", 
                name: "logout", 
                component: LogoutPage, 
                meta: { requiresAuth: false, title: "Log Out", publicAuth: false }, 
            },
            {
                path: ":provider/callback",
                name: "auth",
                component: AuthHandlerView,
                meta: { requiresAuth: true,  title: "Auth Verification", publicAuth: false },
            },
        ],
    },
    {
        path: "/dashboard/",
        name: "dashboard",
        component: () => import("@/views/Dashboard.vue"),
        meta: { requiresAuth: true },
        redirect: { name: "reports" },
        children: [
            { path: "getting-started", name: "getting-started", component: GettingStartedView, meta: { requiresAuth: true, title: "Getting Started" } },

            { path: "reports", name: "reports", component: ReportsView, meta: { requiresAuth: true, title: "Reports" } },
            
            { path: "revenue", component: BaseRevenuePage, meta: { requiresAuth: true },
                children: [                
                    { path: "stats", name: "revenue-stats", component: Stats, meta: { requiresAuth: true,  title: "Revenue Stats"  } },
                    { path: "history", name: "revenue-history", component: History, meta: { requiresAuth: true,  title: "Revenue History"  } },
                    { path: "goals", name: "revenue-goals", component: Goals, meta: { requiresAuth: true,  title: "Revenue Goals"  } },
                ], 
                redirect: { name: "revenue-stats" },
            },
                  
            { path: "projects", name: "projects-view", component: ProjectsView, meta: { requiresAuth: true, title: "Projects" } },
      
            {  path: "projects/:id", name: "project-details-view", component: ProjectsDetailsView, meta: { requiresAuth: true, title: "Project Details" }},

            {  path: "projects/:id/tasks", name: "project-details-tasks", component: ProjectTasksView, title: "Project Tasks",
                children: [
                    {
                        path: ":taskId", // This makes it a child route
                        component: TaskDetailsView,
                        meta: { requiresAuth: true, title: "Task Details", modal: true },
                    },
                ],
            },
                   
            { path: "rate-cards", name:"rate-cards-view", component: RateCardView, meta: { requiresAuth: true, title: "Rate Cards" } },
            
            { path: "tasks", name:"tasks-view", component: TasksView, meta: { requiresAuth: true, title: "Tasks" }},

            { path: "meetings", component: MeetingsView, meta: { requiresAuth: true, title: "Meetings" } },
            
            { path: "invoices", name: "invoices-view", component: InvoicesView, meta: { requiresAuth: true, title: "Invoices" } },
            
            { path: "invoices/create", name: "create-invoice-view", component: CreateEditInvoiceView, meta: { requiresAuth: true, title: "Create Invoice" } },

            { path: "invoices/:id/edit", name: "edit-invoice-view", component: CreateEditInvoiceView, meta: { requiresAuth: true, title: "Edit Invoice" } },
      
            { 
                path: "invoices/:id", 
                name: "invoice-details-view", 
                component: InvoiceDetailsView, 
                meta: { 
                    requiresAuth: true,
                    title: "Invoice Details | Ziner",
                }, 
            },
      
            { path: "clients", name: "clients-view", component: ClientsView, meta: { requiresAuth: true, title: "Clients" } },

            { path: "clients/:id", name:"client-details-view", component: ClientsDetailsView, meta: { requiresAuth: true, title: "Client Details" } },
      
            { path: "settings", name:"settings-view", meta: { requiresAuth: true },
                children: [                
                    { path: "account", name: "account-settings", component: AccountSettings, meta: { requiresAuth: true, title: "Account" }},
                    
                    { path: "general-settings", name: "general-settings", component: GeneralSettings, meta: { requiresAuth: true, title: "General settings" } },

                    { path: "goals-and-productivity", name: "goals-settings", component: GoalsAndProductivity, meta: { requiresAuth: true, title: "Goals & Productivity" } },

                    { path: "payments-and-billings", name: "payment-settings", component: PaymentsAndBillings, meta: { requiresAuth: true, title: "Billings" }},
                    
                    { path: "payments-and-accounts", name: "payment-account-settings", component: PaymentsAccounts, meta: { requiresAuth: true, title: "Billings" }},

                    // { path: "calendars", name: "calendar-settings", component: CalendarView, meta: { requiresAuth: true,  title: "Calendar"  } },

                    { path: "plans", name: "plans-settings", component: Plans, meta: { requiresAuth: true, title: "Pricing & Plans" }},

                    { path: "notifications", name: "notifications-settings", component: Notifications, meta: { requiresAuth: true, title: "Notifications" }},

                    { path: "integrations", name: "integrations-settings", component: Integrations, meta: { requiresAuth: true, title: "Integrations" } },
                ], 
                redirect: { name: "account-settings" },
            },
        ],
    },
    {
        path: "/:catchAll(.*)*",
        name: "error",
        component: () => import("../views/ErrorPage.vue"),
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: "active",
});

// navigation guard for protected routes and dynamic document titles
router.beforeEach(async(to, from, next) => {
    document.title = to.meta.title || "Ziner - Juggle fewer tools & work better";

    const requiresAuth = to.matched.some(r => r.meta.requiresAuth);
    const publicAuth = to.matched.some(r => r.meta.publicAuth);

    try {
        let isAuthenticated = store.getters["auth/isAuthenticated"];

        // if user is authenticated, no need to call the check session endpoint
        if (isAuthenticated) {
            // Authenticated users shouldn't access login/signup pages
            if (publicAuth) {
                return next({ name: "reports", query: { type: "clients", range: "1_year" } });
            }
            return next();
        } 

        // validate for protected or auth-related routes
        if (requiresAuth || publicAuth) {
            try {
                isAuthenticated = await store.dispatch("auth/checkAuth");
            } catch (error) {
                const showNetworkError = store.getters["auth/showNetworkError"];
                
                if (showNetworkError) {                    
                    store.dispatch("auth/setNetworkErr", false);
                    return next({ name: "error", query: { redirect: to.fullPath, error: "network" } });
                }

                throw error;
            }
        }        
        
        // user is still not authenticated
        if (!isAuthenticated) {
            // console.log({ isAuthenticated, publicAuth, requiresAuth });
            
            if (requiresAuth) {
                return next({ name: DEFAULT_AUTH_REDIRECT, query: { redirect: to.fullPath } });
            }

            return next(); // Allow access to public pages
        }
         
        if (publicAuth) {
            return next({ name: to.name, query: { redirect: to.fullPath } });
        }

        return next();
    } catch (err) {
        // console.error("Navigation Guard Error:", err);
        if (publicAuth) {
            return next();
        }

        return next({ name: DEFAULT_AUTH_REDIRECT, query: { redirect: to.fullPath } });
    }
});


export default router;